import { t as uiText } from '../../i18n';
// src/features/collaboration/contentSaveUtils.ts
import { doc, setDoc, serverTimestamp, Bytes, collection, query, where, getDocs, limit, or } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import * as Y from 'yjs';
import { convertYjsToFirestoreData } from './yjsUtils';
import { generateSearchAndPreviewData, SearchData, PreviewData, extractTextFromContentJSON } from './searchDataUtils';
import { generatePreview } from '../../utils/previewGenerator';
import { ASTNode } from '../../types/preview';
import { generateSlug } from '../../utils/slug';
import { assertWorkspaceInvariant } from '../../utils/workspaceInvariant';

interface ContentSaveOptions {
  workspaceId: string;
  boardId: string;
  userId: string;
  ydoc: Y.Doc;
  isHost: boolean;
  saveReason: 'edit-triggered' | 'watchdog-save' | 'final-exit';
  currentTitle?: string; // タイトル情報（検索・プレビューデータ生成用）
  editorContentJSON?: any; // エディタから提供されるcontentJSON
  yjsFirestoreSync?: any; // 編集フラグチェック用（final-exit時のみ使用）
}

/**
 * オブジェクトからundefinedフィールドを再帰的に削除する
 */
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  
  return obj;
}

/**
 * 非同期でembedding生成を実行する関数
 * 保存処理をブロックしないように非同期で実行
 * 
 * @param workspaceId ワークスペースID
 * @param boardId ボードID
 * @param embeddingData 保存時のデータ（直接渡すことでDB読み取りを不要にする）
 * @param yjsFirestoreSync 編集フラグ管理オブジェクト
 */
async function generateEmbeddingAsync(
  workspaceId: string, 
  boardId: string, 
  embeddingData?: {
    searchData?: any;
    contentJSON?: any;
    previewData?: any;
    metadata?: {
      title?: string;
      slug?: string;
      createdAt?: any;
      createdBy?: string;
      lastModified?: any;
      lastModifiedBy?: string;
    };
  },
  yjsFirestoreSync?: any
): Promise<void> {
  try {
    assertWorkspaceInvariant(workspaceId, { source: 'boards.generateEmbeddingAsync' });
    const generateEmbedding = httpsCallable(functions, 'generateEmbeddingFn');

    // 🎯 完全なデータセットを渡す
    const payload: any = {
      workspaceId,
      boardId
    };

    if (embeddingData) {
      // undefinedフィールドを削除してクリーンにする（ログは最小限）
      const cleanedData = removeUndefined(embeddingData);
      
      // データの検証とシリアライズ可能性のチェック（エラー時もログは出さない）
      try {
        if (cleanedData.searchData) {
          JSON.stringify(cleanedData.searchData);  // シリアライズテスト
          payload.searchData = cleanedData.searchData;
        }
      } catch (e) {}
      
      try {
        if (cleanedData.contentJSON) {
          JSON.stringify(cleanedData.contentJSON);  // シリアライズテスト
          payload.contentJSON = cleanedData.contentJSON;
        }
      } catch (e) {}
      
      try {
        if (cleanedData.previewData) {
          JSON.stringify(cleanedData.previewData);  // シリアライズテスト
          payload.previewData = cleanedData.previewData;
        }
      } catch (e) {}
      
      try {
        if (cleanedData.metadata) {
          JSON.stringify(cleanedData.metadata);  // シリアライズテスト
          payload.metadata = cleanedData.metadata;
        }
      } catch (e) {}
    } else {
      // embeddingData がない場合は何もせずに続行
    }

    // 最終的なペイロード全体のシリアライズテスト
    try {
      JSON.stringify(payload);
    } catch (e) {
      throw new Error('Payload serialization failed');
    }

    const result = await generateEmbedding(payload);

    // embedding生成完了後に編集フラグをリセット
    if (yjsFirestoreSync && typeof yjsFirestoreSync.resetContentChangedFlag === 'function') {
      yjsFirestoreSync.resetContentChangedFlag();
    }
  } catch (error: any) {
    // エラーが発生してもアプリケーションの動作は継続（詳細ログは出さない）
  }
}

/**
 * コンテンツデータ保存関数（保存理由に応じて処理を分岐）
 */
export const saveContentToFirestore = async (options: ContentSaveOptions): Promise<boolean> => {
  const { workspaceId, boardId, userId, ydoc, isHost, saveReason, currentTitle, editorContentJSON } = options;

  assertWorkspaceInvariant(workspaceId, { source: 'boards.saveContentToFirestore' });

  if (!ydoc) {
    return false;
  }

  // 🔍 Y.Docの基本状態確認
  const boardIdFromDoc = (ydoc as any).boardId;
  const shareKeys = Array.from(ydoc.share.keys());

  // 各フィールドの内容概要を確認
  const fieldSummary: Record<string, any> = {};
  shareKeys.forEach(key => {
    try {
      const field = ydoc.share.get(key);
      if (field) {
        let summary: any = {
          type: field.constructor.name,
          hasToString: typeof field.toString === 'function'
        };

        if (summary.hasToString) {
          try {
            const content = field.toString();
            summary.contentLength = content.length;
            summary.hasContent = content.trim().length > 0;
            summary.contentSample = content.substring(0, 50) + (content.length > 50 ? '...' : '');
          } catch (e) {
            summary.toStringError = e instanceof Error ? e.message : String(e);
          }
        }

        fieldSummary[key] = summary;
      }
    } catch (error) {
      fieldSummary[key] = { error: error instanceof Error ? error.message : String(error) };
    }
  });

  if (!isHost) {
    return false;
  }

  try {
    // 【二重防御】主要保存処理前の予備検証
    if (boardIdFromDoc && boardIdFromDoc !== 'unknown' && boardIdFromDoc !== 'new') {
      try {
        const { YjsKeyManager } = await import('./yjsKeyManager');
        YjsKeyManager.enforceStrictTyping(ydoc, boardIdFromDoc, { log: false });
      } catch (error) {}
    }

    const { yjsState } = convertYjsToFirestoreData(ydoc);

    // ドキュメントIDの決定
    let targetBoardId = boardId;

    if (boardId === 'new') {
      // 新規作成時は、既存のドキュメントを検索して更新するか、新しく作成するかを決定
      const existingBoardId = await findExistingBoardByTitle(workspaceId, 'Memo_1');
      if (existingBoardId) {
        targetBoardId = existingBoardId;
      } else {
        targetBoardId = generateUniqueId();
      }
    }

    const saveData: any = {
      editData: {
        ydocBytes: Bytes.fromUint8Array(yjsState), // 本文（Yjsドキュメント）
        version: Date.now()
      },
      lastModified: serverTimestamp(),
      lastModifiedBy: userId,
      notificationState: {
        lastEditHeartbeatAt: serverTimestamp(),
        lastEditHeartbeatBy: userId,
        // 編集中ハートビート（通知の create デバウンス用）
        editingHeartbeatAt: serverTimestamp(),
        editingHeartbeatBy: userId
      }
    };

    // 保存理由に応じて検索データとプレビューデータを生成
    let shouldGenerateSearchAndPreview = false;
    let shouldUpdatePreviewMetaOnly = false;
    
    // 🎯 embedding生成用の変数をここで宣言（スコープ問題を回避）
    let finalEditorContentJSON: any = null;
    let enhancedPreviewData: any = null;

    switch (saveReason) {
      case 'edit-triggered':
        // 編集後12秒の保存：previewMetaのみ更新（パフォーマンス重視）
        shouldGenerateSearchAndPreview = false;
        shouldUpdatePreviewMetaOnly = true;
        break;
        
      case 'watchdog-save':
        // 90秒間隔のウォッチドッグ保存：検索・プレビューデータも更新
        shouldGenerateSearchAndPreview = true;
        break;
        
      case 'final-exit':
        // 全員離脱時の保存：検索・プレビューデータも更新
        shouldGenerateSearchAndPreview = true;
        break;
    }

    // 検索データとプレビューデータの生成（必要な場合のみ）
    if (shouldGenerateSearchAndPreview) {
      try {
        // タイトルの安全判定（空 or Memo系は上書きしない）
        const rawTitle = (currentTitle || '').trim();
        const isUnsafeTitle = !rawTitle || /^memo(_\d+)?$/i.test(rawTitle);
        // 生成用タイトル（unsafe時は空を渡し、後段でtitle上書きを抑止）
        const titleToUse = isUnsafeTitle ? '' : rawTitle;
        

        // エディタcontentJSONを取得（優先順位：引数 > Y.Docに保存されたもの）
        finalEditorContentJSON = editorContentJSON;

        if (!finalEditorContentJSON && ydoc) {
          try {
            const savedContentJSON = (ydoc as any)._editorContentJSON;
            const savedTimestamp = (ydoc as any)._editorContentJSONTimestamp;

            // watchdog-saveとfinal-exitは120秒、それ以外は30秒
            const timeLimit = (saveReason === 'watchdog-save' || saveReason === 'final-exit') ? 120000 : 30000;
            const isRecent = savedTimestamp && (Date.now() - savedTimestamp) < timeLimit;

            if (savedContentJSON && isRecent) {
              finalEditorContentJSON = savedContentJSON;
            }
          } catch (error) {}
        }

        // 検索データとプレビューデータの生成（ブラウザ側で完全生成）
        const result = generateSearchAndPreviewData(ydoc, titleToUse, finalEditorContentJSON);
        const previewData = result.previewData;
        const searchData = result.searchData;

        // searchDataをブラウザ側で保存（サーバー側処理を廃止）
        if (searchData) {
          // DateOnPage情報の生成
          const dateOnPage = generateDateOnPage(previewData);

          const baseSearchData: any = {
            originalContent: searchData.originalContent || '',
            normalizedContent: searchData.normalizedContent || '',
            hashtags: searchData.hashtags || [],
            internalLinks: searchData.internalLinks || [],
            externalLinks: searchData.externalLinks || [],
            originalContentTextCount: searchData.originalContentTextCount || 0,
            ...(dateOnPage ? { DateOnPage: dateOnPage } : {}),
            updatedAt: new Date().toISOString(),
            version: 4
          };

          // タイトルフィールドは安全な場合のみ更新
          if (!isUnsafeTitle) {
            baseSearchData.originalTitle = searchData.originalTitle || '';
            baseSearchData.normalizedTitle = searchData.normalizedTitle || '';
          }
          else {}

          saveData.searchData = baseSearchData;
        }

        // 新しいプレビュー生成関数を使用してメタデータを生成
        enhancedPreviewData = previewData;

        if (finalEditorContentJSON) {
          try {
            // contentJSONをASTNode配列として扱う
            let ast: ASTNode[] = [];
            if (finalEditorContentJSON.type === 'doc' && Array.isArray(finalEditorContentJSON.content)) {
              ast = finalEditorContentJSON.content;
            } else if (Array.isArray(finalEditorContentJSON)) {
              ast = finalEditorContentJSON;
            } else {
              console.warn('   ⚠️ AST取得失敗: 未知の形式');
            }

            if (ast.length > 0) {
              const previewMeta = generatePreview(ast);

              // 既存のpreviewDataに新しいメタデータを統合
              const baseStructured = previewData?.structuredData || {
                '@type': 'Article',
                headline: '',
                thumbnailUrl: null,
                wordCount: 0,
                dateCreated: new Date().toISOString(),
                dateModified: new Date().toISOString()
              };

              // タイトル・見出しは安全なときのみ上書き
              const nextTitle = (!isUnsafeTitle ? (titleToUse || previewData?.title) : (previewData?.title));
              const nextStructured = {
                ...baseStructured,
                ...( !isUnsafeTitle ? { headline: titleToUse } : {} )
              } as any;

              if (!isUnsafeTitle) {} else {}

              enhancedPreviewData = {
                // previewData由来の空タイトルを温存しないため、必要項目のみ構築
                ...( nextTitle !== undefined ? { title: nextTitle } : {} ),
                structuredData: nextStructured,
                thumbnailUrl: previewData?.thumbnailUrl || null,
                // 新しいプレビューメタデータで上書き
                previewMeta: {
                  ...previewMeta,
                  generatedAt: serverTimestamp()
                },
                // 従来のcontentフィールドをクリーンアップ済みのテキストで更新
                content: previewMeta.previewText ? [{
                  type: 'paragraph',
                  content: [{
                    type: 'text',
                    text: previewMeta.previewText
                  }]
                }] : (previewData?.content || [])
              } as any;

              // 不安全なタイトル時はtitle/structuredData.headlineを明示的に除去
              if (isUnsafeTitle) {
                if (enhancedPreviewData.title !== undefined) {
                  delete (enhancedPreviewData as any).title;
                }
                if ((enhancedPreviewData as any).structuredData && (enhancedPreviewData as any).structuredData.headline !== undefined) {
                  delete (enhancedPreviewData as any).structuredData.headline;
                }
              }
            } else {
              // AST配列が空のため、previewMeta生成スキップ
            }
          } catch (previewError) {
            // 新しいプレビュー生成に失敗しても既存のpreviewDataを使用
          }
        } else {
          // finalEditorContentJSONが存在しないため、previewMeta生成スキップ
        }

        // searchDataの直接保存は廃止（サーバー側生成）

        if (enhancedPreviewData) {
          saveData.previewData = enhancedPreviewData;
        }

        try {
          const publicRef = doc(db, 'publicPages', workspaceId, 'pages', targetBoardId);
          // ⚠️ タイトルが unsafe（空 or Memo系）な場合に slugAuto を "untitled" で上書きしない
          // - slugAuto は公開URL解決に使われるため、欠落時のみ埋める/安全なタイトルがある時のみ更新する
          const safeSlugAuto = titleToUse ? generateSlug(titleToUse) : '';

          const toSave: any = {
            source: 'editor'  // エディターで作成されたページを識別
          };
          if (safeSlugAuto) {
            toSave.slugAuto = safeSlugAuto;
          }
          // 不安全でない場合のみtitleを書き込む（悪化防止）
          if (!isUnsafeTitle && titleToUse) {
            toSave.title = titleToUse;
          }

          if (finalEditorContentJSON) {
            // doc形式を優先。配列ならdocに包んで保存
            const json = Array.isArray(finalEditorContentJSON)
              ? { type: 'doc', content: finalEditorContentJSON }
              : finalEditorContentJSON;

            if (json && (json.type === 'doc' || json.type === 'Doc' || json.type === 'DOC')) {
              toSave.contentJSON = json;

              const extractedText = extractTextFromContentJSON(json);

              if (extractedText) {
                toSave.text = extractedText;
              }
            } else {
              console.warn('⚠️ [publicPages保存] contentJSONの形式が不正', {
                jsonType: json?.type,
                expectedType: 'doc'
              });
            }
          } else {
            console.warn('⚠️ [publicPages保存] finalEditorContentJSONが存在しません');
          }

          await setDoc(publicRef, toSave, { merge: true });
        } catch (e) {
          console.error('❌ [publicPages保存] 保存に失敗しました（処理継続）:', e);
        }
      } catch (error) {
        console.error('❌ [contentSaveUtils] 検索・プレビューデータ生成エラー:', error);
      }
    }

    // previewMetaのみ更新（edit-triggered時）
    if (shouldUpdatePreviewMetaOnly) {
      try {
        // エディタcontentJSONを取得
        finalEditorContentJSON = editorContentJSON;
        if (!finalEditorContentJSON && ydoc) {
          try {
            const savedContentJSON = (ydoc as any)._editorContentJSON;
            const savedTimestamp = (ydoc as any)._editorContentJSONTimestamp;

            // edit-triggeredは30秒制限（12秒後保存なので十分）
            const timeLimit = 30000;
            const isRecent = savedTimestamp && (Date.now() - savedTimestamp) < timeLimit;

            if (savedContentJSON && isRecent) {
              finalEditorContentJSON = savedContentJSON;
            }
          } catch (error) {}
        }

        if (finalEditorContentJSON) {
          // contentJSONをASTNode配列として扱う
          let ast: ASTNode[] = [];
          if (finalEditorContentJSON.type === 'doc' && Array.isArray(finalEditorContentJSON.content)) {
            ast = finalEditorContentJSON.content;
          } else if (Array.isArray(finalEditorContentJSON)) {
            ast = finalEditorContentJSON;
          }

          if (ast.length > 0) {
            const previewMeta = generatePreview(ast);

            // previewMetaのみ更新
            saveData['previewData.previewMeta'] = {
              ...previewMeta,
              generatedAt: serverTimestamp()
            };
          } else {
            // AST配列が空のため、previewMeta更新スキップ
          }
        } else {
          // finalEditorContentJSONが存在しないため、previewMeta更新スキップ
        }
      } catch (error) {}
    }

    // 🎯 ウォッチドッグ保存（watchdog-save）と全員離脱時（final-exit）にembedding生成を自動実行
    // 実際の発火は本体の setDoc が終わってから。ここで投げると、生成側で例外が出たときに
    // 外側の catch に飲まれて保存そのものが実行されないまま false が返る。
    let pendingEmbedding:
      | { data: Parameters<typeof generateEmbeddingAsync>[2]; sync: any }
      | null = null;
    if (saveReason === 'watchdog-save' || saveReason === 'final-exit') {
      const { yjsFirestoreSync } = options;

      // 編集フラグをチェック
      const hasContentChanged = yjsFirestoreSync && 
        typeof yjsFirestoreSync.hasContentChangedForEmbedding === 'function' ? 
        yjsFirestoreSync.hasContentChangedForEmbedding() : false; // フラグチェックできない場合はスキップ（無駄な実行を防ぐ）

      if (hasContentChanged) {
        // 🎯 完全なデータセットを準備（この時点で持っている全てのデータを渡す）
        // Firebase Functions に渡すため、シリアライズ可能な形式に変換
        
        // searchDataのクリーンアップ（serverTimestamp等を除去）
        const cleanSearchData = saveData.searchData ? {
          originalTitle: saveData.searchData.originalTitle || '',
          normalizedTitle: saveData.searchData.normalizedTitle || '',
          originalContent: saveData.searchData.originalContent || '',
          normalizedContent: saveData.searchData.normalizedContent || '',
          hashtags: saveData.searchData.hashtags || [],
          internalLinks: saveData.searchData.internalLinks || [],
          externalLinks: saveData.searchData.externalLinks || [],
          originalContentTextCount: saveData.searchData.originalContentTextCount || 0,
          DateOnPage: saveData.searchData.DateOnPage ? {
            dateString: saveData.searchData.DateOnPage.dateString || '',
            normalizedDateString: saveData.searchData.DateOnPage.normalizedDateString || '',
            day: saveData.searchData.DateOnPage.day || 0,
            month: saveData.searchData.DateOnPage.month || 0,
            year: saveData.searchData.DateOnPage.year || 0,
            weekday: saveData.searchData.DateOnPage.weekday || ''
          } : undefined
        } : null;
        
        // previewDataのクリーンアップ
        const cleanPreviewData = enhancedPreviewData || saveData.previewData ? (() => {
          const pd = enhancedPreviewData || saveData.previewData;
          return {
            title: pd.title || '',
            previewMeta: pd.previewMeta ? {
              hasImages: pd.previewMeta.hasImages || false,
              imageCount: pd.previewMeta.imageCount || 0,
              firstImageThumb: pd.previewMeta.firstImageThumb || null,
              previewText: pd.previewMeta.previewText || ''
            } : undefined,
            thumbnailUrl: pd.thumbnailUrl || null,
            structuredData: pd.structuredData ? {
              thumbnailUrl: pd.structuredData.thumbnailUrl || null
            } : undefined
          };
        })() : null;
        
        // contentJSONもクリーンに（循環参照や特殊オブジェクトを除去）
        const cleanContentJSON = finalEditorContentJSON ? 
          JSON.parse(JSON.stringify(finalEditorContentJSON)) : null;
        
        const embeddingData = {
          searchData: cleanSearchData,
          contentJSON: cleanContentJSON,
          previewData: cleanPreviewData,
          metadata: {
            title: currentTitle || '',
            slug: generateSlug(currentTitle || 'Untitled'),
            createdAt: new Date().toISOString(),
            createdBy: userId,
            lastModified: new Date().toISOString(),
            lastModifiedBy: userId
          }
        };

        pendingEmbedding = { data: embeddingData, sync: yjsFirestoreSync };
      } else {}
    } else {}

    const boardRef = doc(db, 'workspaces', workspaceId, 'boards', targetBoardId);

    

    await setDoc(boardRef, saveData, { merge: true });

    // await しないのは、embedding 生成の完了を保存レスポンスの待ち時間に含めないため。
    if (pendingEmbedding) {
      void generateEmbeddingAsync(workspaceId, targetBoardId, pendingEmbedding.data, pendingEmbedding.sync);
    }

    return true;
  } catch (error: any) {
    return false;
  }
};

/**
 * DateOnPage情報を生成する関数
 */
const generateDateOnPage = (previewData: any): any | undefined => {
  try {
    const structuredData = previewData?.structuredData;
    const iso = structuredData?.datePublished || structuredData?.eventDate;

    if (!iso || typeof iso !== 'string') {
      return undefined;
    }

    // 日付文字列を解析
    let d: Date;

    if (iso.includes('T') || iso.includes('Z')) {
      // ISO形式の場合
      d = new Date(iso);
    } else if (iso.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // YYYY-MM-DD形式の場合
      d = new Date(iso + 'T00:00:00');
    } else {
      // その他の形式の場合
      d = new Date(iso);
    }

    if (isNaN(d.getTime())) {
      return undefined;
    }

    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const w = [uiText("Sun"),uiText("Mon"),uiText("Tue"),uiText("Wed"),uiText("Thu"),uiText("Fri"),uiText("Sat")][d.getDay()];
    const pad = (n: number) => n.toString().padStart(2, '0');

    const result = {
      dateString: uiText("{{w}}, {{y}}/{{m}}/{{day}}", { y: y, m: m, day: day, w: w }),
      normalizedDateString: `${y}-${pad(m)}-${pad(day)}`,
      year: y,
      month: m,
      day,
      weekday: w,
    };

    return result;
  } catch {
    return undefined;
  }
};

/**
 * 一意のIDを生成する関数
 */
const generateUniqueId = (): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `board_${timestamp}_${random}`;
};

/**
 * タイトルで既存のボードを検索する関数（新DB構造対応）
 */
const findExistingBoardByTitle = async (workspaceId: string, title: string): Promise<string | null> => {
  try {
    // editData.titleとslugの両方で検索
    const boardsRef = collection(db, `workspaces/${workspaceId}/boards`);
    const slug = generateSlug(title);

    // editData.titleまたはslugで検索
    const q = query(
      boardsRef,
      or(
        where('editData.title', '==', title),
        where('slug', '==', slug)
      ),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return doc.id;
    }

    return null;
  } catch {
    return null;
  }
};
